import {
  DialogContent,
  Dialog,
  DialogProps,
  useEventCallback,
  DialogActions,
  useMediaQuery,
  useTheme,
  Grid,
} from '@mui/material';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { PmiButton } from 'shell/components/shared/button';
import { styled } from '@mui/styles';
import { memo } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import PmiTextField from 'shell/components/shared/text-field';
import SearchMessages, { Message, MessageType } from 'shell/components/shared/search-messages';
import { noop } from 'shell/utils/noop';

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

const validationSchema = yup.object({
  reason: yup.string().nullable().required('Campo obbligatorio'),
});

type ModalReasonFormVM = {
  reason: string;
};

interface ModalReasonProps {
  open: boolean;
  onConfirm: (values: ModalReasonFormVM) => void;
  onClose: () => void;
  codeEnte?: string;
  typeRegister?: string;
  yearId?: string;
  progressive?: string;
  typePractice?: string;
  warnings: Message[];
  reason?: string;
}

const ModalReasonComponent = ({
  open,
  onConfirm,
  onClose,
  codeEnte,
  typeRegister,
  typePractice,
  yearId,
  progressive,
  warnings,
  reason,
}: ModalReasonProps): React.ReactElement => {
  const registerNote = useHttpClient(
    `${__PRACTICE_API_URL__}/registrazione-operazione/nota-ultima-operazione`,
    {
      options: { method: 'POST' },
    }
  );

  const onSubmit = useEventCallback((values: ModalReasonFormVM) => {
    const data = {
      codiceEnte: codeEnte || undefined,
      tipoRegistro: typeRegister || undefined,
      idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
      progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      tipoPratica: typePractice || undefined,
      tipoOperazione: '00',
      noteOperazione: values.reason,
    };

    (registerNote({ data }) as ResponsePromise<void>).then(() => {
      onConfirm(values);
    });
  });

  const formContext = useFormik<ModalReasonFormVM>({
    initialValues: {
      reason: reason || '',
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  const onChange = useEventCallback(
    (key: keyof ModalReasonFormVM, value: ModalReasonFormVM[keyof ModalReasonFormVM]): void => {
      formContext.setFieldValue(key, value);
    }
  );

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <PmiDialog
      fullScreen={fullScreen}
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        Motivo pratica cittadino solo minorenne
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <SearchMessages
              type={MessageType.Warning}
              title={`C'è ${warnings?.length || ''} warning`}
              messages={warnings || []}
              onChangeVisibility={noop}
              visibility
            />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              aria-label="Motivo"
              label="Motivo"
              id="note"
              name="note"
              fullWidth
              type="text"
              variant="outlined"
              multiline
              rows={5}
              value={formContext.values?.reason || ''}
              onChange={(event) => onChange('reason', event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <PmiButton variant="contained" color="primary" onClick={() => formContext.handleSubmit()}>
          Conferma
        </PmiButton>
        <PmiButton variant="outlined" color="primary" type="button" onClick={() => onClose()}>
          Annulla
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ModalReasonComponent.displayName = 'Modal reason';

const ModalReason = memo(ModalReasonComponent);

export default ModalReason;
